import { ApolloQueryResult, WatchQueryFetchPolicy } from '@apollo/client';
import {
  GetCollectionsQuery,
  GetCollectionsQueryVariables,
  useGetCollectionsQuery,
  useGetPurchaseCollectionsQuery,
  useGetSubscriptionCollectionsQuery,
} from 'generated/graphql';
import { Async, AsyncError } from '../../helpers/types';
import {
  OTP,
  Subscription,
  RawOTP,
  RawSubscription,
  LiteCollection,
} from './collections.domain';
import { collectionToAsync, mapAsyncCollection } from '../../helpers/gql';
import { F1 } from '../../helpers/types';
import { SalesType } from '@client/new/common/types';
import {
  getAccessType,
  rawDataToLiteCollection as rawDataToLiteCollections,
} from './collectionMappers';
import { makeAsync } from '../../helpers/async';
import { BusinessModel } from '@playbook-technologies/common-enums';
import { FeatureCodeById } from '../programs/model/programs.mappers';

type IdParam = number | null | 'skip';
type CollectionQueryHook<T> = F1<IdParam, Async<Array<T>>>;

const paramError: AsyncError = {
  type: 'fetch-error',
  value: 'invalid :influencerId param',
};

const toQueryOptions = (id: IdParam) => ({
  variables: {
    influencerId: id === 'skip' || id === null ? 0 : id,
    withDeleted: false,
  },
  skip: id === null || id === 'skip',
  fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
});

export const useOTPs: CollectionQueryHook<OTP> = (influencerId) => {
  const res = useGetPurchaseCollectionsQuery(toQueryOptions(influencerId));
  if (influencerId === 'skip') return { type: 'fetched', value: [] };
  if (influencerId === null) return paramError;
  const customURL = res.data?.creator?.customURL || '';
  return mapAsyncCollection(
    collectionToAsync<RawOTP>(res),
    (p) => ({ ...p, customURL }) as OTP,
  );
};

export const useSubscriptions: CollectionQueryHook<Subscription> = (id) => {
  const res = useGetSubscriptionCollectionsQuery(toQueryOptions(id));
  if (id === 'skip') return { type: 'fetched', value: [] };
  if (id === null) return paramError;
  const customURL = res.data?.creator?.customURL || '';
  return mapAsyncCollection(
    collectionToAsync<RawSubscription>(res),
    (p) => ({ ...p, customURL }) as Subscription,
  );
};

const toCollectionQueryOptions = (id: IdParam, type: SalesType) => ({
  variables: {
    influencerId: id === 'skip' || id === null ? 0 : id,
    accessType: getAccessType(type),
    withDeleted: false,
  },
  skip: id === null || id === 'skip',
  fetchPolicy: 'no-cache' as WatchQueryFetchPolicy,
});

export type Refetch = (
  variables?: Partial<GetCollectionsQueryVariables>,
) => Promise<ApolloQueryResult<GetCollectionsQuery>>;

type UseCollections = {
  liteCollections: Array<LiteCollection>;
  businessModel: BusinessModel | null;
  isOtpMigratedToBundle: boolean;
  bundles: GetCollectionsQuery['bundle'];
  featureCodeById: FeatureCodeById;
  refetch: Refetch;
};

export const useCollections = (
  influencerId: IdParam,
  type: SalesType,
): Async<UseCollections> => {
  const res = useGetCollectionsQuery(
    toCollectionQueryOptions(influencerId, type),
  );
  if (influencerId === null) return paramError;
  const customURL = res.data?.creator?.customURL || '';
  const profileDeepLink = res.data?.creator?.profileDeepLink || '';
  const collections = res.data?.collections || [];
  const businessModel =
    (res.data?.creator?.businessModel as BusinessModel) || null;
  const isOtpMigratedToBundle =
    res.data?.creator?.customizations?.isOtpMigratedToBundle ?? false;

  if (res.error) return makeAsync.error(res.error.message);
  if (res.loading) return makeAsync.fetching();

  const liteCollections = rawDataToLiteCollections(
    collections,
    customURL,
    profileDeepLink,
  );

  const featureCodeById: FeatureCodeById = new Map(
    (res.data?.creator?.features ?? [])
      .filter((f): f is NonNullable<typeof f> => Boolean(f))
      .map((f) => [f.id, f.code] as const),
  );

  return {
    type: 'fetched',
    value: {
      liteCollections,
      refetch: res.refetch,
      businessModel,
      isOtpMigratedToBundle,
      bundles: res.data?.bundle || [],
      featureCodeById,
    },
  };
};
